import { useMutation, useQuery } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { Button } from '@bb/ui';
import { ApiError, type AdminApi } from '../../api/client.ts';

const useApi = () => (useRouter().options.context as { api: AdminApi }).api;

/** The tag that links a block (the same text the service returns as `tag`). */
export const contentBlockTag = (key: string) => `{% content_block '${key}' %}`;

/**
 * Content blocks in the personalization picker (BB-57). «Link» inserts the tag: the message renders the block's
 * version pinned at launch. «Copy» inserts the current body as plain text, with no link to the block.
 */
export function ContentBlockPicker({ environmentId, query, onInsert }: { environmentId: string; query: string; onInsert: (text: string) => void }) {
  const api = useApi();
  const list = useQuery({ queryKey: ['content-blocks', environmentId, { query: '', archived: false }], queryFn: () => api.listContentBlocks(environmentId, { query: '', archived: false }) });
  const copy = useMutation({ mutationFn: (blockId: string) => api.getContentBlock(environmentId, blockId), onSuccess: (block) => onInsert(block.current.body) });
  const q = query.trim().toLowerCase();
  const blocks = (list.data?.items ?? []).filter((b) => !q || b.name.toLowerCase().includes(q) || b.key.includes(q));
  if (list.isPending || list.isError || blocks.length === 0) return null;
  return (
    <section aria-label="Content blocks">
      <h4>
        Content blocks <span className="hint">{blocks.length}</span>
      </h4>
      <ul className="plain-list">
        {blocks.map((b) => (
          <li key={b.id} className="variable-picker__item">
            <code>{b.key}</code>{' '}
            <span className="hint">
              {b.name} · v{b.currentVersion}
            </span>{' '}
            <Button size="sm" variant="ghost" aria-label={`Link block ${b.name}`} onClick={() => onInsert(contentBlockTag(b.key))}>
              Link
            </Button>
            <Button size="sm" variant="ghost" aria-label={`Copy block ${b.name}`} disabled={copy.isPending} onClick={() => copy.mutate(b.id)}>
              Copy
            </Button>
          </li>
        ))}
      </ul>
      {copy.isError && <p role="alert">The block could not be copied{copy.error instanceof ApiError ? `: ${copy.error.message}` : '.'}</p>}
    </section>
  );
}
